import Auth from "@/services/auth/auth";
import { VERIFY_MFA } from "@/store/action-types";
import {
  CLEAR_MFA_PENDING,
  SET_ACCOUNT,
  SET_MFA_PENDING,
  SET_SESSION_TOKEN,
  SET_REFRESH_TOKEN,
} from "@/store/mutation-types";
import { clearOnboardingChecklistDismissed } from "@/utils/onboardingChecklistStorage";
import { MfaPendingState, VerifyMfaPayload } from "@/dtos/Auth.dto";
import { AxiosResponse } from "axios";
import { Module } from "vuex";

// Deliberately NOT included in the vuex-persistedstate `paths` list (see
// store/index.ts) — the pending token is short-lived (10 min) and should not
// survive a page refresh or outlive the browser tab.
const mfa: Module<any, {}> = {
  state: {
    pendingToken: null,
    mfaMethods: [],
  },
  getters: {
    mfaPendingToken: (state: { pendingToken: any }) => state.pendingToken,
    mfaMethods: (state: { mfaMethods: any }) => state.mfaMethods,
  },
  mutations: {
    [SET_MFA_PENDING]: (
      state: { pendingToken: any; mfaMethods: any },
      payload: MfaPendingState
    ) => {
      state.pendingToken = payload.pendingToken;
      state.mfaMethods = payload.mfaMethods || [];
    },
    [CLEAR_MFA_PENDING]: (state: { pendingToken: any; mfaMethods: any }) => {
      state.pendingToken = null;
      state.mfaMethods = [];
    },
  },
  actions: {
    [VERIFY_MFA]: async ({ commit, state }: any, payload: VerifyMfaPayload) => {
      if (!state.pendingToken) {
        throw new Error("Your verification session has expired. Please log in again.");
      }

      commit(SET_ACCOUNT, { data: null, isLoading: true });

      try {
        const result: AxiosResponse = await Auth.verifyMfa(state.pendingToken, payload);

        if (result?.data?.status) {
          commit(SET_SESSION_TOKEN, result.data.sessionToken);
          commit(SET_REFRESH_TOKEN, result.data.refreshToken);
          const accountData = {
            ...result.data.data,
            loginMethod: result.data.loginMethod || null,
          };
          commit(SET_ACCOUNT, { data: accountData, isLoading: false });
          commit(CLEAR_MFA_PENDING);
          clearOnboardingChecklistDismissed(accountData.id);
          return true;
        }

        commit(SET_ACCOUNT, { data: null, isLoading: false });
        throw new Error(result?.data?.msg || "Verification failed. Please try again.");
      } catch (error: any) {
        commit(SET_ACCOUNT, { data: null, isLoading: false });

        if (error.response) {
          const errorMessage =
            error.response.data?.errorMessage ||
            error.response.data?.message ||
            error.message ||
            "Verification failed. Please try again.";

          const enhancedError = new Error(errorMessage);
          (enhancedError as any).response = error.response;
          throw enhancedError;
        }

        throw error;
      }
    },
  },
};

export default mfa;
